import { datadogLogs } from '@datadog/browser-logs';
import type { LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { getDatadogConfig } from './config';

// Internal logging helper to avoid circular dependency with logger
const internalLog = (level: 'debug' | 'info' | 'warn' | 'error', msg: string, ctx?: Record<string, unknown>): void => {
  const entry = { ts: new Date().toISOString(), level, msg, component: 'Datadog', ...ctx };
  /* eslint-disable no-console */
  if (level === 'error') console.error(JSON.stringify(entry));
  else if (level === 'warn') console.warn(JSON.stringify(entry));
  else if (level === 'debug') console.debug(JSON.stringify(entry));
  else console.log(JSON.stringify(entry));
  /* eslint-enable no-console */
};

interface DatadogConfig {
  clientToken: string;
  applicationId?: string; // For RUM
  site?: string; // datadoghq.eu or datadoghq.com
  service?: string;
  env?: string;
  version?: string;
  sampleRate?: number; // RUM sample rate (0-100)
  enableRum?: boolean;
}

let isInitialized = false;

/**
 * Initialize Datadog Logs and RUM
 *
 * Should be called early in the app lifecycle (e.g., in main.tsx)
 *
 * @param config - Datadog configuration
 */
export function initDatadog(config?: DatadogConfig): void {
  if (isInitialized) {
    internalLog('warn', 'Datadog already initialized', { action: 'init' });
    return;
  }

  // Use provided config or load from environment
  const finalConfig = config || getDatadogConfig();

  if (!finalConfig.clientToken) {
    internalLog('warn', 'Datadog: Client token not provided, skipping initialization', { action: 'init' });
    return;
  }

  // Don't initialize in localhost
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      internalLog('debug', 'Datadog: Skipping initialization on localhost', { action: 'init' });
      return;
    }
  }

  const site = (finalConfig.site || 'datadoghq.eu') as 'datadoghq.eu' | 'datadoghq.com' | 'us3.datadoghq.com' | 'us5.datadoghq.com' | 'ap1.datadoghq.com' | 'ddog-gov.com';
  const service = finalConfig.service || 'tangi-frontend';
  const env = finalConfig.env || import.meta.env.MODE || 'development';

  // Initialize Datadog Logs
  const logsConfig: LogsInitConfiguration = {
    clientToken: finalConfig.clientToken,
    site,
    service,
    env,
    version: finalConfig.version,
    forwardErrorsToLogs: true, // Automatically forward console errors
    forwardConsoleLogs: 'all', // Forward all console logs
    forwardReports: 'all', // Forward CSP, CORS, etc.
    sessionSampleRate: 100, // 100% of sessions (adjust for production)
    beforeSend: () => {
      // Return true to send the log, false to drop it
      // Context is added via setGlobalContext/addGlobalContext
      return true;
    },
  };

  try {
    datadogLogs.init(logsConfig);

    // Add global context after initialization
    datadogLogs.setGlobalContext({
      service,
      env,
      ...(finalConfig.version && { version: finalConfig.version }),
    });

    internalLog('info', 'Datadog Logs initialized', { action: 'init_logs' });
  } catch (error) {
    internalLog('error', 'Failed to initialize Datadog Logs', {
      action: 'init_logs',
      error: error instanceof Error ? error.message : String(error),
    });
  }

  // Initialize Datadog RUM (if enabled and applicationId provided)
  if (finalConfig.enableRum && finalConfig.applicationId) {
    const rumConfig: RumInitConfiguration = {
      applicationId: finalConfig.applicationId,
      clientToken: finalConfig.clientToken,
      site,
      service,
      env,
      version: finalConfig.version,
      sessionSampleRate: finalConfig.sampleRate || 100, // 100% sample rate (adjust for production)
      trackUserInteractions: true, // Track user interactions
      trackResources: true, // Track resource loading
      trackLongTasks: true, // Track long tasks
      defaultPrivacyLevel: 'mask-user-input', // Mask sensitive input
      beforeSend: () => {
        // Return true to send the event, false to drop it
        // Context is added via setGlobalContext/addGlobalContext
        return true;
      },
    };

    try {
      datadogRum.init(rumConfig);

      // Add global context after initialization
      datadogRum.setGlobalContext({
        service,
        env,
        ...(finalConfig.version && { version: finalConfig.version }),
      });

      datadogRum.startSessionReplayRecording(); // Start session replay
      internalLog('info', 'Datadog RUM initialized', { action: 'init_rum' });
    } catch (error) {
      internalLog('error', 'Failed to initialize Datadog RUM', {
        action: 'init_rum',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  isInitialized = true;
}

/**
 * Check if Datadog is initialized
 */
export function isDatadogInitialized(): boolean {
  return isInitialized;
}

/**
 * Log a message to Datadog
 */
export function logToDatadog(
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  context?: Record<string, unknown>
): void {
  if (!isInitialized) {
    return;
  }

  try {
    datadogLogs.logger[level](message, context);
  } catch (error) {
    internalLog('error', 'Failed to log to Datadog', {
      action: 'log',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
